import { useLocation } from "wouter";
import { useAuthMe, type ProjectType } from "@vorloop/api-client-react";
import { NavRail } from "../components/layout/NavRail";
import { WorkspaceSwitcher } from "../components/layout/WorkspaceSwitcher";
import { IconMobile, IconWebsite } from "../components/layout/Icons";

/**
 * Template catalog. Each card carries a project type + a prefilled
 * prompt the agent will run on first turn. Clicking "Use this"
 * navigates to the dashboard with the prompt pre-staged via
 * sessionStorage — Dashboard reads it and starts the create flow.
 */
interface Template {
  readonly id: string;
  readonly title: string;
  readonly tagline: string;
  readonly type: ProjectType;
  readonly prompt: string;
  readonly icon: React.ReactNode;
}

const TEMPLATES: ReadonlyArray<Template> = [
  {
    id: "mobile-netflix",
    title: "Streaming app",
    tagline: "Netflix-style mobile UI with hero banner, content rows, and bottom tabs.",
    type: "mobile",
    prompt:
      "Build a Netflix-clone mobile app with React Native + Expo Router. Bottom tabs: Home, Search, Downloads, Profile. Home has a hero banner with featured content, multiple horizontally-scrolling content rows (Trending, Top 10, Continue Watching). Use Netflix's signature black (#000) + red (#E50914) palette. Add proper tab navigation using the (tabs) group route.",
    icon: <IconMobile size={18} />,
  },
  {
    id: "mobile-todo",
    title: "Todo list",
    tagline: "Persisted tasks, swipe to delete, priority labels.",
    type: "mobile",
    prompt:
      "Build a polished todo-list mobile app. Add task with title + priority (Low/Med/High). Swipe to delete. Filter chips. Use a clean modern dark theme.",
    icon: <IconMobile size={18} />,
  },
  {
    id: "mobile-pomodoro",
    title: "Pomodoro timer",
    tagline: "25/5 sessions, audio cue, session count.",
    type: "mobile",
    prompt:
      "Build a Pomodoro timer mobile app. Big circular progress ring, 25-minute work and 5-minute break sessions, session counter, gentle audio cue on switch.",
    icon: <IconMobile size={18} />,
  },
  {
    id: "web-portfolio",
    title: "Portfolio site",
    tagline: "One-page personal site with hero, projects grid, contact.",
    type: "web",
    prompt:
      "Build a one-page personal portfolio site. Hero with name + tagline, projects grid (3-column on desktop), skills section, contact form. Modern dark theme.",
    icon: <IconWebsite size={18} />,
  },
  {
    id: "web-landing",
    title: "SaaS landing",
    tagline: "Hero, feature grid, pricing tiers, CTA, FAQ.",
    type: "web",
    prompt:
      "Build a SaaS landing page. Hero with H1 + subhead + email signup, 3-column feature grid, 3-tier pricing cards, testimonial strip, FAQ section.",
    icon: <IconWebsite size={18} />,
  },
  {
    id: "web-dashboard",
    title: "Admin dashboard",
    tagline: "Sidebar nav, KPI cards, line chart, recent activity table.",
    type: "web",
    prompt:
      "Build an admin dashboard layout. Left sidebar nav (Home, Users, Orders, Settings). Top metrics row: 4 KPI cards. Big line chart placeholder. Recent activity table below.",
    icon: <IconWebsite size={18} />,
  },
];

export function Templates() {
  const me = useAuthMe();
  const handle = me.data?.user?.handle ?? "you";
  const [, setLocation] = useLocation();

  function useTemplate(t: Template) {
    try {
      sessionStorage.setItem("vp.template.prompt", t.prompt);
      sessionStorage.setItem("vp.template.type", t.type);
    } catch {
      // private mode → user can paste manually
    }
    setLocation("/");
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", background: "var(--bg-canvas)" }}>
      <NavRail handle={handle} />
      <main
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        <header
          style={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "10px 24px",
          }}
        >
          <WorkspaceSwitcher handle={handle} compact />
        </header>

        <div
          style={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            padding: "32px 32px 80px",
          }}
        >
          <div style={{ width: "100%", maxWidth: 1080, display: "flex", flexDirection: "column", gap: 24 }}>
            <header>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.01em" }}>
                Templates
              </h1>
              <p style={{ color: "var(--text-tertiary)", fontSize: 13, marginTop: 6 }}>
                Start from a working scaffold. The prompt pre-fills on the dashboard so you can tune
                it before generating.
              </p>
            </header>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 14,
              }}
            >
              {TEMPLATES.map((t) => (
                <article
                  key={t.id}
                  style={{
                    border: "1px solid var(--border-default)",
                    background: "var(--bg-panel)",
                    borderRadius: 12,
                    padding: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: "var(--bg-panel-2)",
                      border: "1px solid var(--border-default)",
                      display: "grid",
                      placeItems: "center",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {t.icon}
                  </div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{t.title}</div>
                    <div
                      style={{
                        color: "var(--text-tertiary)",
                        fontSize: 12,
                        marginTop: 4,
                        lineHeight: 1.45,
                        minHeight: 32,
                      }}
                    >
                      {t.tagline}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: "auto" }}>
                    <TypePill type={t.type} />
                    <div style={{ flex: 1 }} />
                    <button
                      type="button"
                      onClick={() => useTemplate(t)}
                      style={{
                        padding: "6px 12px",
                        background: "var(--accent-blue)",
                        color: "#fff",
                        border: "none",
                        borderRadius: 7,
                        fontSize: 12,
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      Use this template
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function TypePill({ type }: { type: ProjectType }) {
  const tone: Record<ProjectType, { bg: string; fg: string }> = {
    mobile: { bg: "rgba(99,102,241,0.12)", fg: "#a5b4fc" },
    web: { bg: "rgba(16,185,129,0.12)", fg: "#34d399" },
    component: { bg: "rgba(245,158,11,0.12)", fg: "#fbbf24" },
    backend: { bg: "rgba(248,113,113,0.12)", fg: "#fca5a5" },
  };
  const t = tone[type];
  return (
    <span
      style={{
        padding: "2px 8px",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        background: t.bg,
        color: t.fg,
        borderRadius: 999,
      }}
    >
      {type}
    </span>
  );
}
